import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';

interface UsuarioPrimeiroAcesso {
  id: string;
  email: string;
  nome_completo: string;
  primeiro_acesso: boolean;
  senha_temporaria: boolean;
  senha_expira_em: string | null;
  cipa_facil: boolean;
  app_st: boolean;
}

export const useFirstAccess = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isPrimeiroAcesso, setIsPrimeiroAcesso] = useState(false);
  const [dadosUsuario, setDadosUsuario] = useState<UsuarioPrimeiroAcesso | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [isCipaFacilUser, setIsCipaFacilUser] = useState(false);

  // Verificar se é primeiro acesso
  const verificarPrimeiroAcesso = async () => {
    if (!user) {
      setLoading(false);
      setIsCipaFacilUser(false);
      return;
    }

    try {
      setLoading(true);
      setErro(null);

      // Primeiro tentar buscar do auth.users com metadata (nova estrutura)
      // Usar a função RPC que combina dados do auth.users e cp_usuarios
      const { data: authData, error: authError } = await supabase
        .rpc('get_user_cipa_data', { user_id: user.id });

      let userData = null;

      if (!authError && authData && authData.length > 0) {
        // Dados encontrados na nova estrutura (auth.users)
        userData = authData[0];
        console.log('🔍 Dados encontrados no auth.users:', userData);
      } else {
        // Fallback: buscar na cp_usuarios (estrutura antiga)
        console.log('⚠️ Fallback: buscando na cp_usuarios');
        const { data: cpData, error: cpError } = await supabase
          .from('cp_usuarios')
          .select(`
            id,
            email,
            nome_completo,
            primeiro_acesso,
            senha_temporaria,
            senha_expira_em
          `)
          .eq('id', user.id)
          .single();

        if (cpError) {
          throw cpError;
        }

        if (cpData) {
          // Simular estrutura auth.users para compatibilidade
          userData = {
            ...cpData,
            cipa_facil: true, // Assumir que está na cp_usuarios = CIPA FÁCIL
            app_st: false
          };
        }
      }

      if (!userData) {
        throw new Error('Usuário não encontrado em nenhuma tabela');
      }

      // Verificar se é usuário do CIPA FÁCIL
      // Se veio do fallback (cp_usuarios), já é considerado CIPA FÁCIL
      // Se veio da função RPC, verificar o campo cipa_facil
      const isCipaUser = userData.cipa_facil === true || userData.cipa_facil === undefined;
      setIsCipaFacilUser(isCipaUser);

      if (!isCipaUser) {
        throw new Error('Usuário não tem permissão para acessar o CIPA FÁCIL');
      }

      setDadosUsuario(userData);
      
      // Verificar se é primeiro acesso e senha não expirou
      const senhaExpirada = userData.senha_expira_em && 
        new Date(userData.senha_expira_em) < new Date();

      if (senhaExpirada) {
        throw new Error('Senha temporária expirada. Entre em contato com o administrador.');
      }

      setIsPrimeiroAcesso(userData.primeiro_acesso && userData.senha_temporaria);

    } catch (err) {
      console.error('Erro ao verificar primeiro acesso:', err);
      setErro(err instanceof Error ? err.message : 'Erro ao verificar dados do usuário');
      setIsCipaFacilUser(false);
    } finally {
      setLoading(false);
    }
  };

  // Marcar primeiro acesso como concluído
  const concluirPrimeiroAcesso = async () => {
    if (!user || !dadosUsuario) return;

    try {
      // Usar a função RPC para atualizar metadata e cp_usuarios
      const { error } = await supabase
        .rpc('resetar_senha_primeiro_acesso_metadata', {
          usuario_id: user.id,
          nova_senha: '' // Senha já foi alterada pelo auth.updateUser
        });

      if (error) {
        console.warn('Erro na RPC, tentando fallback:', error);
        
        // Fallback: atualizar cp_usuarios diretamente
        const { error: fallbackError } = await supabase
          .from('cp_usuarios')
          .update({
            primeiro_acesso: false,
            senha_temporaria: false,
            senha_expira_em: null,
            ultimo_acesso: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (fallbackError) {
          throw fallbackError;
        }
      }

      // Atualizar estado local
      setDadosUsuario(prev => prev ? {
        ...prev,
        primeiro_acesso: false,
        senha_temporaria: false,
        senha_expira_em: null
      } : null);

      setIsPrimeiroAcesso(false);

    } catch (err) {
      console.error('Erro ao concluir primeiro acesso:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (user) {
      verificarPrimeiroAcesso();
    } else {
      setLoading(false);
      setIsPrimeiroAcesso(false);
      setDadosUsuario(null);
      setIsCipaFacilUser(false);
    }
  }, [user]);

  return {
    loading,
    isPrimeiroAcesso,
    dadosUsuario,
    erro,
    isCipaFacilUser,
    verificarPrimeiroAcesso,
    concluirPrimeiroAcesso
  };
};
